import { useRef, useState, type TouchEvent as ReactTouchEvent } from 'react';

/**
 * Swipe a card off the screen with a finger (owner, 24 Sep 2026: "เอานิ้วปัดออกไม่ให้บัง").
 *
 * Used by the notifications that float over the app — the new-chat cards and the toasts. v1.79.0: any direction
 * clears it, because the owner reached for up and down on the phone and nothing happened ("อยากให้ปัดขึ้นบนหรือล่าง
 * ก็เป็นการเคลียได้"). The first clear movement picks the axis and the card then travels only along it, and the
 * decision is made from a ref rather than state because the last touchmove may not have been rendered yet
 * (same lesson as the inbox row swipe). The cards carry `touch-action: none` so both axes are ours to read.
 */
const TAKE_OVER_PX = 10;
const DISMISS_PX = 72;
const FADE_OVER_PX = 200;

export function useSwipeAway(onDismiss: () => void) {
  const [offset, setOffset] = useState<{ x: number; y: number } | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const moved = useRef(0);
  const axis = useRef<'x' | 'y' | null>(null);

  const reset = () => {
    start.current = null;
    moved.current = 0;
    axis.current = null;
    setOffset(null);
  };

  const onTouchStart = (e: ReactTouchEvent) => {
    const t = e.touches[0];
    if (!t) return;
    start.current = { x: t.clientX, y: t.clientY };
    moved.current = 0;
    axis.current = null;
  };

  const onTouchMove = (e: ReactTouchEvent) => {
    const t = e.touches[0];
    if (!t || !start.current) return;
    const dx = t.clientX - start.current.x;
    const dy = t.clientY - start.current.y;
    if (!axis.current) {
      const far = Math.max(Math.abs(dx), Math.abs(dy));
      if (far < TAKE_OVER_PX) return;
      axis.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    const along = axis.current === 'x' ? dx : dy;
    moved.current = along;
    setOffset(axis.current === 'x' ? { x: along, y: 0 } : { x: 0, y: along });
  };

  const onTouchEnd = () => {
    if (axis.current && Math.abs(moved.current) >= DISMISS_PX) {
      onDismiss();
      reset();
      return;
    }
    reset();
  };

  const far = offset ? Math.abs(offset.x || offset.y) : 0;
  return {
    /** Spread onto the card. */
    handlers: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel: reset },
    style: far
      ? {
          transform: `translate(${offset?.x ?? 0}px, ${offset?.y ?? 0}px)`,
          opacity: Math.max(0.15, 1 - far / FADE_OVER_PX),
          transition: 'none',
        }
      : undefined,
  };
}
